"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import Link from "@/components/no-prefetch-link";
import { useRouter, useSearchParams } from "next/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Sparkles, ArrowUp, Clock, Flame, ExternalLink, Bell, Star, CalendarDays, ChevronRight } from "lucide-react";
import { AffinityChip } from "@/components/affinity-chip";
import { SubscriptionDrawer } from "@/components/subscription-drawer";
import { AiInsightSection } from "@/components/ai-insight-section";
import { secureGet } from "@/lib/security/fetch-client";
import { TopicEmotionBadge, EmotionCacheProvider } from "@/components/topic-emotion-badge";
import { encodeId, decodeId } from "@/lib/id-encoder";
import { trackTopicClick, trackWeiboSearch, trackTabSwitch } from "@/lib/analytics";
import { usePageTag } from "@/lib/use-page-tag";
import {
  enhanceTopicItems,
  getAffinityTypeName,
  type RelatedTopicLink,
  type TopicAffinityLabel,
} from "@/lib/topic-affinity";

// TopicDrawer 是模态侧栏,初始 closed,且内部依赖 recharts (~360KB)。
// 懒加载到用户点击 topic 时再下载,把 First Load JS 从 387KB 降到 ~280KB。
// ssr:false 防止服务端水合 drawer 内容(它本身就是客户端交互组件)。
const TopicDrawer = dynamic(
  () => import("@/components/topic-drawer").then((m) => ({ default: m.TopicDrawer })),
  { ssr: false }
);

export interface TopicItem {
  id: number;
  name: string;
  rank: number;
  hotindex: number;
  sessionCount: number;
  durationHours: number;
  currentRank?: number;
  previousRank?: number | null;
  rankChange?: number;
  isNew?: boolean;
  starttime?: Date;
  affinityLabels?: TopicAffinityLabel[];
  relatedTopics?: RelatedTopicLink[];
}

function formatHotindex(num: number): string {
  if (num >= 10000000) return (num / 10000000).toFixed(1) + "千万";
  if (num >= 10000) return (num / 10000).toFixed(1) + "万";
  return num.toString();
}

function formatDuration(hours: number): string {
  if (hours < 1) return "<1小时";
  if (hours < 24) return `${hours}小时`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}天${remainHours}小时` : `${days}天`;
}

function getRankBadge(rank: number) {
  if (rank === 1) return <Badge className="bg-red-500 text-white">1</Badge>;
  if (rank === 2) return <Badge className="bg-orange-500 text-white">2</Badge>;
  if (rank === 3) return <Badge className="bg-yellow-500 text-white">3</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">{rank}</Badge>;
}

interface HotlistTabsProps {
  initialData?: TopicItem[];
  /** 历史 timeid(历史模式传入);有值时仅显示"综合"tab,隐藏上升最快/新上榜 */
  timeid?: number;
  /** 是否在卡片顶部嵌入 AI 速览(仅实时模式) */
  showAiInsight?: boolean;
}

export function HotlistTabs({ initialData = [], timeid, showAiInsight = false }: HotlistTabsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const topicParam = searchParams.get("topic");
  usePageTag("home");
  const [activeTab, setActiveTab] = useState<"all" | "rising" | "new" | "followed">("all");
  const [allData, setAllData] = useState<TopicItem[]>(enhanceTopicItems(initialData));
  const [risingData, setRisingData] = useState<TopicItem[]>([]);
  const [newData, setNewData] = useState<TopicItem[]>([]);
  const [followedData, setFollowedData] = useState<TopicItem[]>([]);
  const [followedLoading, setFollowedLoading] = useState(false);
  const [followedKeywords, setFollowedKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState({ all: initialData.length === 0, rising: false, new: false });

  // 侧边栏状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);

  // Top1 AI 速览：自动获取榜首话题的 AI 摘要
  const [topBrief, setTopBrief] = useState<{ headline: string; brief: string } | null>(null);
  useEffect(() => {
    const topItem = allData[0];
    if (!topItem || !showAiInsight) { setTopBrief(null); return; }
    let cancelled = false;
    const encoded = encodeId(topItem.id);
    secureGet<{ brief: { content?: { headline: string; brief: string } } | null }>("/api/ai/brief", { topicId: encoded })
      .then((res) => {
        if (cancelled || !res.brief?.content) return;
        setTopBrief({ headline: res.brief.content.headline, brief: res.brief.content.brief });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [allData, showAiInsight]);

  // 订阅侧边栏状态
  const [subDrawerOpen, setSubDrawerOpen] = useState(false);

  // 从 URL 读取初始状态 (已编码)
  // 用 ref 追踪已处理的 topic，避免 searchParams 引用变化触发无限循环
  const processedTopicRef = useRef<string | null>(null);
  useEffect(() => {
    // 跳过：无 topic 参数 / 已处理过相同 topic
    if (!topicParam || topicParam === processedTopicRef.current) return;
    const topicId = decodeId(topicParam);
    if (topicId !== null) {
      processedTopicRef.current = topicParam;
      setSelectedTopicId(topicId);
      setDrawerOpen(true);
    }
  }, [topicParam]);

  // 加载关注词并匹配当前榜单
  const loadFollowedTopics = useCallback(async () => {
    setFollowedLoading(true);
    try {
      const data = await secureGet<{ subscriptions: Array<{ keyword: string; type: number }> }>("/api/subscriptions");
      const keywords = data.subscriptions.map((s) => s.keyword);
      setFollowedKeywords(keywords);
      if (keywords.length === 0) {
        setFollowedData([]);
        return;
      }
      // 从当前榜单中筛选匹配关注词的词条
      const matched = allData.filter((item) =>
        keywords.some((kw) => item.name.includes(kw) || kw.includes(item.name))
      );
      setFollowedData(matched);
    } catch {
      setFollowedData([]);
    } finally {
      setFollowedLoading(false);
    }
  }, [allData]);

  const handleTopicClick = (id: number, name?: string, rank?: number) => {
    setSelectedTopicId(id);
    setDrawerOpen(true);
    trackTopicClick(name || "", rank, activeTab);
    // 更新 URL - 使用编码 ID
    const url = new URL(window.location.href);
    url.searchParams.set("topic", encodeId(id));
    router.replace(url.pathname + url.search, { scroll: false });
  };

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) {
      // 关闭抽屉后保留当前 topic 已处理标记，等待 URL 真正移除。
      // 否则 router.replace 尚未完成时，useSearchParams 仍读到旧 topic，
      // 会把刚关闭的抽屉再次打开，旧 Safari/内嵌浏览器里可能进一步放大为 React #185。
      processedTopicRef.current = topicParam;
      // 清除 URL 参数
      const url = new URL(window.location.href);
      url.searchParams.delete("topic");
      router.replace(url.pathname + url.search, { scroll: false });
    }
  };

  const fetchAllData = useCallback(async () => {
    if (allData.length > 0) return;
    setLoading((prev) => ({ ...prev, all: true }));
    try {
      const json = await secureGet<{ time: string; data: TopicItem[] }>("/api/hotlist", { limit: 50 });
      setAllData(enhanceTopicItems(json.data || []));
    } catch (e) {
      console.error("Failed to fetch hotlist:", e);
    } finally {
      setLoading((prev) => ({ ...prev, all: false }));
    }
  }, [allData.length]);

  const fetchRisingData = useCallback(async () => {
    if (risingData.length > 0) return;
    setLoading((prev) => ({ ...prev, rising: true }));
    try {
      const params: Record<string, string | number | boolean> = { limit: 50 };
      if (timeid) params.timeid = timeid;
      const json = await secureGet<{ data: TopicItem[] }>("/api/ranking/rising", params);
      setRisingData(enhanceTopicItems(json.data || []));
    } catch (e) {
      console.error("Failed to fetch rising ranking:", e);
    } finally {
      setLoading((prev) => ({ ...prev, rising: false }));
    }
  }, [risingData.length, timeid]);

  const fetchNewData = useCallback(async () => {
    if (newData.length > 0) return;
    setLoading((prev) => ({ ...prev, new: true }));
    try {
      const params: Record<string, string | number | boolean> = { limit: 50, hours: 24 };
      if (timeid) params.timeid = timeid;
      const json = await secureGet<{ data: TopicItem[] }>("/api/ranking/new", params);
      setNewData(enhanceTopicItems(json.data || []));
    } catch (e) {
      console.error("Failed to fetch new ranking:", e);
    } finally {
      setLoading((prev) => ({ ...prev, new: false }));
    }
  }, [newData.length, timeid]);

  useEffect(() => {
    if (activeTab === "all") {
      fetchAllData();
    } else if (activeTab === "rising") {
      fetchRisingData();
    } else if (activeTab === "new") {
      fetchNewData();
    }
  }, [activeTab, fetchAllData, fetchRisingData, fetchNewData]);

  // 首次只加载当前 Tab
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { key: "all" as const, label: "综合", icon: Sparkles },
    // 历史模式(有 timeid)仅显示综合,隐藏上升最快/新上榜(这两者依赖实时对比)
    ...(timeid ? [] : [
      { key: "rising" as const, label: "上升最快", icon: TrendingUp },
      { key: "new" as const, label: "新上榜", icon: ArrowUp },
      { key: "followed" as const, label: "关注", icon: Star },
    ]),
  ];

  const renderTopStory = (item: TopicItem | undefined) => {
    if (!showAiInsight || activeTab !== "all" || !item) return null;
    return (
      <section className="mb-2.5 hidden overflow-hidden rounded-lg border bg-gradient-to-r from-red-50 via-background to-amber-50 dark:from-red-950/20 dark:via-background dark:to-amber-950/20 sm:block">
        <button
          type="button"
          onClick={() => handleTopicClick(item.id, item.name, item.rank)}
          className="group flex w-full flex-col gap-2 p-3 text-left transition-colors hover:bg-white/45 dark:hover:bg-white/5"
        >
          {/* 头部：标签 + 标题 */}
          <div className="flex items-start gap-3">
            <Badge className="mt-0.5 shrink-0 bg-red-500 text-white">头条</Badge>
            <div className="min-w-0 flex-1">
              <h2 className="break-words text-lg font-semibold leading-snug tracking-tight group-hover:text-primary sm:text-xl">
                {item.name}
              </h2>
              {/* AI 速览摘要 */}
              {topBrief && (
                <p className="mt-1 text-sm leading-6 text-foreground/75 line-clamp-2">
                  {topBrief.brief}
                </p>
              )}
            </div>
          </div>

          {/* 底部：元数据 + CTA */}
          <div className="flex items-center justify-between gap-3 pl-[calc(1.5rem+0.75rem)]">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-medium text-orange-600">
                <Flame className="h-3 w-3" />
                {formatHotindex(item.hotindex)}
              </span>
              <span>在榜 {formatDuration(item.durationHours || 0)}</span>
              {(item.sessionCount || 1) > 1 && <span>{item.sessionCount} 次上榜</span>}
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-slate-950 px-2.5 py-1.5 text-xs font-medium text-white transition-colors group-hover:bg-primary dark:bg-white dark:text-slate-950">
              {topBrief ? "阅读全文" : "看解读"}
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </button>
      </section>
    );
  };

  const renderList = (data: TopicItem[], type: "all" | "rising" | "new" | "followed", isLoading: boolean) => {
    // 已有数据时不显示骨架屏，避免切换闪烁
    if (isLoading && data.length === 0) {
      return (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      );
    }

    if (data.length === 0) {
      if (type === "followed") {
        return (
          <div className="text-center py-8 space-y-3">
            <Star className="h-8 w-8 mx-auto text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {followedKeywords.length === 0
                ? "添加关注词后，匹配的热搜会出现在这里。"
                : "当前榜单没有匹配关注词的词条。"}
            </p>
            <button
              onClick={() => setSubDrawerOpen(true)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {followedKeywords.length === 0 ? "添加关注词" : "管理关注词"}
            </button>
          </div>
        );
      }
      return (
        <div className="text-center text-muted-foreground py-8">
          暂无数据
        </div>
      );
    }

    return (
      <div className="divide-y divide-border/60">
        {data.map((item, index) => {
          const showMobileTopBrief = showAiInsight && activeTab === "all" && index === 0 && topBrief;
          return (
          <div key={item.id} className={`transition-colors hover:bg-muted/45 ${showMobileTopBrief ? "border bg-gradient-to-r from-red-50/70 via-background to-amber-50/70 sm:border-0 sm:bg-none" : ""}`}>
            <div
              onClick={() => handleTopicClick(item.id, item.name, item.rank)}
              className="flex cursor-pointer items-center gap-2 p-2.5 group sm:gap-4 sm:px-4 sm:py-3"
            >
              {/* 排名 */}
              <div className="w-7 flex-shrink-0 sm:w-8">
                {getRankBadge(item.rank)}
              </div>

              {/* 词条名称 */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 truncate text-sm font-medium transition-colors group-hover:text-primary sm:text-base">
                  <span>{item.name}</span>
                  <TopicEmotionBadge topicId={item.id} />
                </div>
                {showMobileTopBrief && (
                  <div className="mt-1.5 flex gap-1.5 text-xs leading-5 text-muted-foreground sm:hidden">
                    <span className="mt-0.5 inline-flex h-4 shrink-0 items-center rounded bg-slate-900 px-1 text-[10px] font-semibold leading-4 text-white">
                      AI
                    </span>
                    <span className="line-clamp-2">{topBrief.brief}</span>
                  </div>
                )}
                {type === "rising" && item.rankChange && (
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-green-600">
                    <ArrowUp className="h-3 w-3" />
                    上升 {item.rankChange} 名
                    {item.isNew && (
                      <Badge variant="secondary" className="ml-1 px-1 py-0 text-[10px]">
                        新上榜
                      </Badge>
                    )}
                  </div>
                )}
                {type === "new" && item.starttime && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    首次上榜: {new Date(item.starttime).toLocaleString("zh-CN")}
                  </div>
                )}

                {((item.affinityLabels?.length || 0) > 0 || (item.relatedTopics?.length || 0) > 0) && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {/* 移动端只显1个affinity+N,桌面显3个 */}
                    {(item.affinityLabels || []).slice(0, 3).map((label, idx) => (
                      <AffinityChip
                        key={`${label.type}:${label.name}`}
                        label={label}
                        className={idx >= 1 ? "hidden sm:inline-flex" : "inline-flex"}
                      />
                    ))}
                    {(item.affinityLabels?.length || 0) > 1 && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground sm:hidden">
                        +{(item.affinityLabels?.length || 0) - 1}
                      </span>
                    )}
                    {(item.affinityLabels?.length || 0) > 3 && (
                      <span className="hidden rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground sm:inline-flex">
                        +{(item.affinityLabels?.length || 0) - 3}
                      </span>
                    )}
                    {/* relatedTopics 移动端隐藏 */}
                    {(item.relatedTopics || []).map((topic) => (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleTopicClick(topic.id, topic.name);
                        }}
                        className="hidden rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary sm:inline-flex"
                        title={topic.reason}
                      >
                        相关：{topic.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 热度 */}
              <div className="flex w-[5.5rem] flex-shrink-0 items-center justify-end gap-1 text-sm font-semibold text-orange-500 sm:w-24 sm:text-base">
                <Flame className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{formatHotindex(item.hotindex)}</span>
              </div>

              <a
                href={`https://s.weibo.com/weibo?q=${encodeURIComponent(item.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => { event.stopPropagation(); trackWeiboSearch("hotlist", item.name); }}
                className="flex items-center justify-center rounded-md border px-2 py-1 text-xs text-muted-foreground hover:text-primary sm:inline-flex sm:gap-1"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">微博</span>
              </a>

              {/* 上榜次数 & 在榜时长 - 移动端隐藏 */}
              <div className="hidden w-16 flex-shrink-0 items-center justify-end gap-3 text-xs text-muted-foreground sm:flex">
                {(item.sessionCount || 1) > 1 && (
                  <div className="flex items-center gap-1" title="上榜次数">
                    <TrendingUp className="h-3 w-3" />
                    <span>{item.sessionCount}次</span>
                  </div>
                )}
                <div className="flex items-center gap-1" title="在榜时长">
                  <Clock className="h-3 w-3" />
                  <span>{formatDuration(item.durationHours || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        );
        })}
      </div>
    );
  };

  const currentData = activeTab === "all" ? allData : activeTab === "rising" ? risingData : activeTab === "new" ? newData : followedData;
  const currentLoading = activeTab === "followed" ? followedLoading : loading[activeTab];

  return (
    <EmotionCacheProvider>
      <Card className="gap-0 overflow-hidden rounded-lg py-0 shadow-sm sm:rounded-xl">
        {showAiInsight && (
          <div className="hidden sm:block">
            <AiInsightSection embedded />
          </div>
        )}
        <CardHeader className="px-2 pb-2 pt-2 sm:px-4 sm:py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-lg bg-muted/60 p-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      trackTabSwitch(tab.key, "hotlist");
                      if (tab.key === "followed" && followedKeywords.length === 0) void loadFollowedTopics();
                    }}
                    className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                      activeTab === tab.key
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-1">
              {showAiInsight && !timeid && (
                <Link
                  href="/daily"
                  className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:px-3 sm:text-sm"
                  title="查看全天热点一览"
                >
                  <CalendarDays className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="inline">日榜</span>
                </Link>
              )}
              {/* 订阅入口 */}
              <button
                onClick={() => setSubDrawerOpen(true)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:px-3 sm:text-sm"
                title="订阅热搜提醒"
              >
                <Bell className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">订阅</span>
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 pb-3 sm:px-4 sm:pb-5">
          {renderTopStory(allData[0])}
          {renderList(currentData, activeTab, currentLoading)}
        </CardContent>
      </Card>

      {/* 侧边栏 */}
      <TopicDrawer
        topicId={selectedTopicId}
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
      />

      {/* 订阅管理侧边栏 */}
      <SubscriptionDrawer open={subDrawerOpen} onOpenChange={setSubDrawerOpen} />
    </EmotionCacheProvider>
  );
}
